<template>
  <div :class="$style.container">
    <!-- 통계 탭 네비게이션 -->
    <StatsTabs />

    <!-- 로딩 상태 -->
    <div v-if="loading">
      <LoadingScreen loading-text="아티스트 통계를 불러오는 중..." />
    </div>

    <!-- 에러 상태 -->
    <div v-else-if="error" :class="$style.error">
      <p>{{ error }}</p>
      <button @click="fetchArtistStats" :class="$style.retryButton">
        다시 시도
      </button>
    </div>

    <!-- 통계 데이터 -->
    <div v-else>
      <div :class="$style.header">
        <div :class="$style.titleSection">
          <h1 :class="$style.title">🎵 아티스트 통계</h1>
          <p :class="$style.subtitle">등록된 라이브의 아티스트별 출연 횟수</p>
        </div>
      </div>
      <!-- 요약 정보 -->
      <div :class="$style.summary">
        <div :class="$style.summaryCard">
          <h3>총 아티스트</h3>
          <p :class="$style.summaryNumber">{{ totalArtists }}</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>총 라이브 연결</h3>
          <p :class="$style.summaryNumber">{{ totalLives }}</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>가장 많이 출연</h3>
          <p :class="$style.summaryNumber">{{ topArtist?.name || "-" }}</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>보컬 아티스트</h3>
          <p :class="$style.summaryNumber">{{ vocalArtists }}</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>밴드 아티스트</h3>
          <p :class="$style.summaryNumber">{{ bandArtists }}</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>믹스 아티스트</h3>
          <p :class="$style.summaryNumber">{{ mixArtists }}</p>
        </div>
      </div>

      <!-- 검색 및 필터 -->
      <div :class="$style.controls">
        <div :class="$style.searchBox">
          <input v-model="searchQuery" type="text" placeholder="아티스트명으로 검색..." :class="$style.searchInput" />
        </div>
        <div :class="$style.typeFilter">
          <button :class="[
            $style.typeButton,
            { [$style.activeType]: selectedTypes.all },
          ]" @click="toggleType('all')">
            전체
          </button>
          <button :class="[
            $style.typeButton,
            { [$style.activeType]: selectedTypes.vocal },
          ]" @click="toggleType('vocal')">
            보컬
          </button>
          <button :class="[
            $style.typeButton,
            { [$style.activeType]: selectedTypes.band },
          ]" @click="toggleType('band')">
            밴드
          </button>
          <button :class="[
            $style.typeButton,
            { [$style.activeType]: selectedTypes.mix },
          ]" @click="toggleType('mix')">
            믹스
          </button>
          <button :class="[
            $style.typeButton,
            $style.pendingButton,
            { [$style.activeType]: showPendingOnly },
          ]" @click="togglePendingFilter">
            승인 대기
          </button>
        </div>
        <Dropdown v-model="sortBy" :options="sortOptions" optionLabel="label" optionValue="value"
          placeholder="정렬 기준을 선택하세요" :class="$style.sortDropdown" :showClear="true" />
      </div>

      <!-- 아티스트 목록 -->
      <div :class="$style.artistList">
        <div v-for="(artist, index) in paginatedArtists" :key="artist.name" :class="$style.artistCard">
          <div :class="$style.rank">{{ index + 1 }}</div>
          <div :class="$style.artistInfo">
            <!-- 프로필 사진 섹션 -->
            <ProfileImageUploader :artist-name="artist.name" :profile-image="artist.profileImage"
              :show-controls="isAdmin" @image-updated="(url) => handleImageUpdated(artist, url)"
              @image-removed="() => handleImageRemoved(artist)" />

            <h3 :class="$style.artistName">{{ artist.name }}</h3>
            <p :class="$style.artistCount">
              {{ `총 ${artist.count}회 출연` }}
            </p>
            <div v-if="artist.count > 0" :class="$style.liveList">
              <span v-for="live in artist.lives" :key="live.id" :class="$style.liveTag" @click="goToLiveDetail(live.id)"
                style="cursor: pointer">
                <img :src="typeIconUrl(live.type)" :alt="live.type" :class="$style.typeIcon" />
                {{ live.title }}
              </span>
            </div>
            <div v-else :class="$style.noLivesMessage">
              <span>등록된 공연이 없습니다</span>
            </div>

            <!-- 작성자 정보 섹션 -->
            <div v-if="artist.regNickname || artist.regId" :class="$style.authorSection">
              <div :class="$style.authorInfo">
                <span :class="$style.authorLabel">등록자:</span>
                <span :class="$style.authorName">{{
                  artist.regNickname || artist.regId
                }}</span>
                <span :class="$style.registrationSource">{{
                  getRegistrationSourceLabel(artist.registrationSource)
                }}</span>
                <span v-if="artist.createdAt" :class="$style.authorDate">
                  {{ formatDate(artist.createdAt) }}
                </span>
              </div>
              <!-- 수정자 정보 (등록자와 다른 경우에만 표시) -->
              <div v-if="
                artist.lastModifiedByNickname &&
                artist.lastModifiedByNickname !==
                (artist.regNickname || artist.regId)
              " :class="$style.authorInfo">
                <span :class="$style.authorLabel">최근 수정:</span>
                <span :class="$style.modifierName">{{
                  artist.lastModifiedByNickname || artist.lastModifiedBy
                }}</span>
                <span v-if="artist.updatedAt" :class="$style.authorDate">
                  {{ formatDate(artist.updatedAt) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 페이지네이션 -->
      <div v-if="totalPages > 1" :class="$style.pagination">
        <button :class="$style.pageButton" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">
          ‹
        </button>

        <button v-for="page in pageRange" :key="page" :class="[
          $style.pageButton,
          { [$style.activePage]: page === currentPage },
        ]" @click="goToPage(page)">
          {{ page }}
        </button>

        <button :class="$style.pageButton" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">
          ›
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  collection,
  getDocs,
} from "firebase/firestore";
import { ref as storageRef } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { firestore, storage } from "/firebaseConfig";
import { useUserStore } from "@/store/user";
import Dropdown from "primevue/dropdown";
import ProfileImageUploader from "@/components/ProfileImageUploader.vue";
import LoadingScreen from "@/components/LoadingScreen.vue";
import StatsTabs from "@/components/StatsTabs.vue";
import {
  ARTIST_POSITION_OPTIONS,
  ARTIST_TYPE_OPTIONS_WITH_BAND,
  getArtistTypeLabel,
} from "@/utils/artistTypes";

export default {
  name: "ArtistStats",
  components: {
    Dropdown,
    ProfileImageUploader,
    LoadingScreen,
    StatsTabs,
  },
  data() {
    return {
      loading: true,
      error: null,
      artists: [],
      sortOptions: [
        { label: "출연 횟수순", value: "count" },
        { label: "이름순", value: "name" },
        { label: "최근 출연순", value: "recent" },
        { label: "등록순", value: "registered" },
      ],
      searchQuery: "",
      sortBy: "count",
      currentPage: 1,
      itemsPerPage: 20,
      selectedTypes: { all: true, vocal: false, band: false, mix: false },
      showPendingOnly: false, // 승인 대기 필터 상태
      // Dropdown용 타입 옵션들
      typeOptions: ARTIST_TYPE_OPTIONS_WITH_BAND,
      // 멤버 관리용 데이터
      allArtists: [],
      artistsWithTypes: [], // 아티스트 이름과 타입 정보

      // 포지션 옵션
      availablePositions: ARTIST_POSITION_OPTIONS,
    };
  },
  computed: {
    userStore() {
      return useUserStore();
    },

    // 관리자 권한 확인
    isAdmin() {
      return this.userStore.auth === "admin";
    },

    // 검색 및 정렬된 아티스트 목록
    filteredArtists() {
      let filtered = [...this.artists];

      // 타입 필터 (승인 대기 필터가 활성화되지 않았을 때만 적용)
      if (!this.selectedTypes.all && !this.showPendingOnly) {
        filtered = filtered.filter((artist) => {
          // 출연한 라이브가 있는 경우: 라이브 타입으로 필터링
          if (artist.lives && artist.lives.length > 0) {
            return artist.lives.some((live) => {
              if (this.selectedTypes.vocal && live.type === "vocal")
                return true;
              if (this.selectedTypes.band && live.type === "band") return true;
              if (this.selectedTypes.mix && live.type === "mix") return true;
              return false;
            });
          }
          // 출연한 라이브가 없는 경우: 아티스트 자체의 타입으로 필터링
          else {
            if (this.selectedTypes.vocal && artist.selectedType === "vocal")
              return true;
            if (this.selectedTypes.band && artist.selectedType === "band")
              return true;
            if (this.selectedTypes.mix && artist.selectedType === "mix")
              return true;
            return false;
          }
        });
      }

      // 승인 대기 필터
      if (this.showPendingOnly) {
        filtered = filtered.filter((artist) => !artist.approved && artist.id);
      }

      // 검색 필터
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter((artist) =>
          artist.name.toLowerCase().includes(query)
        );
      }

      // 정렬
      switch (this.sortBy) {
        case "count":
          filtered.sort((a, b) => {
            if (a.count === 0 && b.count === 0) {
              return a.name.localeCompare(b.name); // 둘 다 출연 횟수가 0이면 이름순
            }
            if (a.count === 0) return 1; // a가 0이면 뒤로
            if (b.count === 0) return -1; // b가 0이면 뒤로
            return b.count - a.count; // 둘 다 0이 아니면 출연 횟수순
          });
          break;
        case "name":
          filtered.sort((a, b) => a.name.localeCompare(b.name));
          break;
        case "recent":
          filtered.sort((a, b) => {
            // lastAppearance가 없는 경우 처리
            if (!a.lastAppearance && !b.lastAppearance) {
              return a.name.localeCompare(b.name);
            }
            if (!a.lastAppearance) return 1;
            if (!b.lastAppearance) return -1;
            return new Date(b.lastAppearance) - new Date(a.lastAppearance);
          });
          break;
        case "registered":
          filtered.sort((a, b) => {
            // 등록일시가 최신인 것이 먼저 오도록 (내림차순)
            const aDate = a.createdAt
              ? new Date(a.createdAt.seconds * 1000 || a.createdAt)
              : new Date(0);
            const bDate = b.createdAt
              ? new Date(b.createdAt.seconds * 1000 || b.createdAt)
              : new Date(0);
            return bDate - aDate;
          });
          break;
      }

      return filtered;
    },

    // 페이지네이션된 아티스트 목록
    paginatedArtists() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      return this.filteredArtists.slice(startIndex, endIndex);
    },

    // 총 페이지 수
    totalPages() {
      return Math.ceil(this.filteredArtists.length / this.itemsPerPage);
    },

    // 페이지 범위
    pageRange() {
      const total = this.totalPages;
      const current = this.currentPage;

      if (total <= 5) {
        const pages = [];
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
        return pages;
      }

      let start, end;

      if (current <= 3) {
        start = 1;
        end = 5;
      } else if (current >= total - 2) {
        start = total - 4;
        end = total;
      } else {
        start = current - 2;
        end = current + 2;
      }

      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },

    // 요약 통계
    totalArtists() {
      return this.artists.length;
    },

    totalLives() {
      return this.artists.reduce((total, artist) => total + artist.count, 0);
    },

    topArtist() {
      if (this.artists.length === 0) return null;
      return this.artists.reduce((top, current) =>
        current.count > top.count ? current : top
      );
    },

    // 타입별 아티스트 수
    vocalArtists() {
      return this.artists.filter((artist) => {
        // 라이브에 출연한 경우: 라이브 타입 확인
        if (artist.lives && artist.lives.length > 0) {
          return artist.lives.some((live) => live.type === "vocal");
        }
        // 라이브에 출연하지 않은 경우: 아티스트 타입 확인
        return artist.selectedType === "vocal";
      }).length;
    },

    bandArtists() {
      return this.artists.filter((artist) => {
        // 라이브에 출연한 경우: 라이브 타입 확인
        if (artist.lives && artist.lives.length > 0) {
          return artist.lives.some((live) => live.type === "band");
        }
        // 라이브에 출연하지 않은 경우: 아티스트 타입 확인
        return artist.selectedType === "band";
      }).length;
    },

    mixArtists() {
      return this.artists.filter((artist) => {
        // 라이브에 출연한 경우: 라이브 타입 확인
        if (artist.lives && artist.lives.length > 0) {
          return artist.lives.some((live) => live.type === "mix");
        }
        // 라이브에 출연하지 않은 경우: 아티스트 타입 확인
        return artist.selectedType === "mix";
      }).length;
    },
  },

  async created() {
    if (!this.isAdmin) {
      this.$router.push("/");
      return;
    }
    await this.fetchArtistStats();
  },

  watch: {
    searchQuery() {
      this.currentPage = 1;
      // 검색어 변경 시 필터링된 아티스트의 이미지 로드
      this.$nextTick(() => {
        this.loadProfileImagesForCurrentPage();
      });
    },
    sortBy() {
      this.currentPage = 1;
      // 정렬 변경 시 재정렬된 아티스트의 이미지 로드
      this.$nextTick(() => {
        this.loadProfileImagesForCurrentPage();
      });
    },
    selectedTypes: {
      handler() {
        this.currentPage = 1;
        // 타입 필터 변경 시 필터링된 아티스트의 이미지 로드
        this.$nextTick(() => {
          this.loadProfileImagesForCurrentPage();
        });
      },
      deep: true,
    },
    currentPage() {
      // 페이지 변경 시 해당 페이지의 이미지 로드
      this.loadProfileImagesForCurrentPage();
    },
    showPendingOnly() {
      this.currentPage = 1;
      // 승인 대기 필터 변경 시 필터링된 아티스트의 이미지 로드
      this.$nextTick(() => {
        this.loadProfileImagesForCurrentPage();
      });
    },
  },

  methods: {
    async fetchArtistStats() {
      try {
        this.loading = true;
        this.error = null;

        // 모든 라이브 데이터 가져오기
        const livesSnapshot = await getDocs(collection(firestore, "lives"));

        // 모든 아티스트 데이터 가져오기 (이미지 URL 생성 제외)
        const artistsSnapshot = await getDocs(collection(firestore, "artists"));
        const artistDetailsMap = new Map();

        // 아티스트 상세 정보를 Map으로 저장 (이미지 URL 생성 제외)
        artistsSnapshot.docs.forEach((doc) => {
          const artistData = doc.data();
          artistDetailsMap.set(artistData.name, {
            detailLink: artistData.detailLink || null,
            imgFileName: artistData.img || null, // 파일명만 저장
            userProfileImg: artistData.userProfileImg || null, // 사용자 프로필 이미지 파일명
            userEmail: artistData.userEmail || null, // 사용자 이메일
            type: artistData.type || null,
            category: artistData.category || null, // category 필드 추가
            members: artistData.members || null, // 밴드 멤버 정보 추가
            updatedAt: artistData.updatedAt || null,
            approved: artistData.approved || false, // 승인 상태 추가
            id: doc.id, // 문서 ID 추가 (승인 처리용)
            // 작성자 정보 추가
            regId: artistData.regId || artistData.createdBy || null,
            regNickname:
              artistData.regNickname || artistData.createdByNickname || null,
            createdAt: artistData.createdAt || null,
            // 수정자 정보 추가
            lastModifiedBy: artistData.lastModifiedBy || null,
            lastModifiedByNickname: artistData.lastModifiedByNickname || null,
            // 등록 소스 정보 추가
            registrationSource: artistData.registrationSource || "unknown",
          });
        });

        // 아티스트별 데이터 집계
        const artistMap = new Map();

        // 먼저 /artist 컬렉션의 모든 아티스트를 artistMap에 추가
        artistDetailsMap.forEach((artistDetails, artistName) => {
          artistMap.set(artistName, {
            name: artistName,
            count: 0,
            lives: [],
            firstAppearance: null,
            lastAppearance: null,
            detailLink: artistDetails.detailLink || null,
            profileImage: null, // 초기에는 null
            imgFileName: artistDetails.imgFileName || null, // 파일명 저장
            userProfileImg: artistDetails.userProfileImg || null, // 사용자 프로필 이미지 파일명
            userEmail: artistDetails.userEmail || null, // 사용자 이메일
            profileImageLoaded: false, // 이미지 로드 상태
            selectedType: artistDetails.type || null, // 저장된 타입
            tempSelectedType: artistDetails.type || null, // 임시 선택 타입
            type: artistDetails.type || null, // type 필드 직접 추가
            category: artistDetails.category || null, // category 필드 직접 추가
            // 멤버 관리 관련 필드 추가
            members: artistDetails.members || null, // 저장된 멤버 목록
            tempMembers: artistDetails.members
              ? [...artistDetails.members]
              : [], // 임시 멤버 목록
            membersEditing: false, // 멤버 수정 모드 상태
            membersSaving: false, // 멤버 저장 중 상태
            currentMemberInput: "", // 현재 입력 중인 멤버명
            filteredMembers: [], // 필터링된 멤버 후보 목록
            editing: false,
            tempDetailLink: "",
            saving: false,
            uploading: false,
            typeSaving: false, // 타입 저장 중 상태
            typeEditing: false, // 타입 수정 모드 상태
            tempCategory: "",
            tempPositions: [],
            deleting: false, // 삭제 중 상태
            approved: artistDetails.approved || false, // 승인 상태
            id: artistDetails.id || null, // 문서 ID
            // 작성자 정보 추가
            regId: artistDetails.regId || null,
            regNickname: artistDetails.regNickname || null,
            createdAt: artistDetails.createdAt || null,
            // 수정자 정보 추가
            lastModifiedBy: artistDetails.lastModifiedBy || null,
            lastModifiedByNickname:
              artistDetails.lastModifiedByNickname || null,
            // 등록 소스 정보 추가
            registrationSource: artistDetails.registrationSource || "unknown",
          });
        });

        // 라이브 데이터에서 아티스트 출연 정보 업데이트
        livesSnapshot.docs.forEach((doc) => {
          const liveData = doc.data();
          const lineup = liveData.lineup || [];

          if (Array.isArray(lineup)) {
            lineup.forEach((artistName) => {
              if (!artistName || typeof artistName !== "string") return;

              const trimmedName = artistName.trim();
              if (!trimmedName) return;

              // 아티스트가 /artist 컬렉션에 없으면 추가
              if (!artistMap.has(trimmedName)) {
                artistMap.set(trimmedName, {
                  name: trimmedName,
                  count: 0,
                  lives: [],
                  firstAppearance: null,
                  lastAppearance: null,
                  detailLink: null,
                  profileImage: null, // 초기에는 null
                  imgFileName: null, // 파일명 저장
                  userProfileImg: null, // 사용자 프로필 이미지 파일명
                  userEmail: null, // 사용자 이메일
                  profileImageLoaded: false, // 이미지 로드 상태
                  selectedType: null, // 저장된 타입
                  tempSelectedType: null, // 임시 선택 타입
                  // 멤버 관리 관련 필드 추가
                  members: null, // 저장된 멤버 목록
                  tempMembers: [], // 임시 멤버 목록
                  membersEditing: false, // 멤버 수정 모드 상태
                  membersSaving: false, // 멤버 저장 중 상태
                  currentMemberInput: "", // 현재 입력 중인 멤버명
                  filteredMembers: [], // 필터링된 멤버 후보 목록
                  editing: false,
                  tempDetailLink: "",
                  saving: false,
                  uploading: false,
                  typeSaving: false, // 타입 저장 중 상태
                  typeEditing: false, // 타입 수정 모드 상태
                  deleting: false, // 삭제 중 상태
                  approved: true, // 라이브에 출연한 아티스트는 이미 활동 중으로 간주
                  id: null, // artists 컬렉션에 없는 아티스트는 ID가 없음
                });
              }

              const artist = artistMap.get(trimmedName);
              artist.count++;

              const liveInfo = {
                id: doc.id,
                title: liveData.title,
                date: liveData.date,
                type: liveData.type,
              };

              artist.lives.push(liveInfo);

              // 날짜 파싱 및 최초/최근 출연일 업데이트
              try {
                const dateStr = liveData.date;
                if (dateStr) {
                  const normalizedDate = dateStr.replace(/\./g, "-");
                  const liveDate = new Date(normalizedDate);

                  if (!isNaN(liveDate.getTime())) {
                    if (
                      !artist.firstAppearance ||
                      liveDate < new Date(artist.firstAppearance)
                    ) {
                      artist.firstAppearance = dateStr;
                    }
                    if (
                      !artist.lastAppearance ||
                      liveDate > new Date(artist.lastAppearance)
                    ) {
                      artist.lastAppearance = dateStr;
                    }
                  }
                }
              } catch (e) {
                console.error("날짜 파싱 오류:", e);
              }
            });
          }
        });

        // Map을 배열로 변환하고 출연 횟수순으로 정렬 (출연 횟수가 0인 아티스트는 이름순으로)
        this.artists = Array.from(artistMap.values()).sort((a, b) => {
          if (a.count === 0 && b.count === 0) {
            return a.name.localeCompare(b.name); // 둘 다 출연 횟수가 0이면 이름순
          }
          if (a.count === 0) return 1; // a가 0이면 뒤로
          if (b.count === 0) return -1; // b가 0이면 뒤로
          return b.count - a.count; // 둘 다 0이 아니면 출연 횟수순
        });

        // 첫 페이지 아티스트들의 프로필 이미지만 미리 로드
        await this.loadProfileImagesForCurrentPage();

        // 아티스트 목록 로드 (멤버 관리용)
        await this.loadArtistsForMembers();
      } catch (error) {
        console.error("아티스트 통계 조회 오류:", error);
        this.error = "데이터를 불러오는 중 오류가 발생했습니다.";
      } finally {
        this.loading = false;
      }
    },

    // 현재 페이지의 아티스트들만 프로필 이미지 로드
    async loadProfileImagesForCurrentPage() {
      const currentPageArtists = this.paginatedArtists;

      // 로드해야 할 아티스트들 필터링 (imgFileName 또는 userProfileImg가 있는 경우)
      const artistsToLoad = currentPageArtists.filter(
        (artist) =>
          (artist.imgFileName || artist.userProfileImg) &&
          !artist.profileImageLoaded
      );

      if (artistsToLoad.length === 0) {
        return; // 로드할 이미지가 없으면 종료
      }

      // console.log(`프로필 이미지 로딩 시작: ${artistsToLoad.length}개 아티스트`);

      // 병렬로 이미지 URL 생성
      const imageLoadPromises = artistsToLoad.map(async (artist) => {
        try {
          let imageRef;
          let imagePath;

          // 사용자 프로필 이미지가 있으면 profile 폴더에서, 아니면 artist 폴더에서 로드
          if (artist.userProfileImg) {
            imagePath = `profile/${artist.userProfileImg}`;
            imageRef = storageRef(storage, imagePath);
            // console.log(`사용자 프로필 이미지 로드 시도: ${artist.name} -> ${imagePath}`);
          } else if (artist.imgFileName) {
            imagePath = `artist/${artist.imgFileName}`;
            imageRef = storageRef(storage, imagePath);
            // console.log(`아티스트 이미지 로드 시도: ${artist.name} -> ${imagePath}`);
          }

          if (imageRef) {
            const profileImageUrl = await getDownloadURL(imageRef);
            artist.profileImage = profileImageUrl;
            artist.profileImageLoaded = true;
            // console.log(`프로필 이미지 로드 완료: ${artist.name}`);
          }
        } catch (error) {
          console.warn(`프로필 이미지 URL 생성 실패 (${artist.name}):`, error);
          artist.profileImage = null;
          artist.profileImageLoaded = true; // 실패해도 로드 시도했음을 표시
        }
      });

      await Promise.all(imageLoadPromises);
      // console.log(
      //   `프로필 이미지 로딩 완료: ${artistsToLoad.length}개 아티스트`
      // );
    },

    formatDate(date) {
      if (!date) return "-";
      try {
        // 이미 문자열 형태의 날짜인 경우 그대로 반환
        if (typeof date === "string") {
          return date;
        }
        // Date 객체인 경우 한국 형식으로 변환
        if (date instanceof Date) {
          return date.toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
        }
        return "-";
      } catch (e) {
        console.error("날짜 포맷 오류:", e);
        return "-";
      }
    },

    // 등록 소스 라벨 반환
    getRegistrationSourceLabel(source) {
      switch (source) {
        case "artist-form":
          return "📝 신청폼";
        case "my-profile":
          return "👤 마이프로필";
        case "admin":
          return "🛡️ 관리자";
        default:
          return "❓ 미상";
      }
    },

    async goToPage(page) {
      this.currentPage = page;
      // 페이지 변경 후 이미지 로드는 watch에서 처리됨
    },

    toggleType(type) {
      // 타입 필터 선택 시 승인 대기 필터 해제
      this.showPendingOnly = false;

      if (type === "all") {
        // 전체 선택 시 다른 모든 타입 해제
        this.selectedTypes = {
          all: true,
          vocal: false,
          band: false,
          mix: false,
        };
      } else {
        // 특정 타입 선택 시 전체 해제하고 해당 타입만 선택
        this.selectedTypes = {
          all: false,
          vocal: false,
          band: false,
          mix: false,
        };
        this.selectedTypes[type] = true;
      }
      this.currentPage = 1; // 필터 변경 시 첫 페이지로
    },

    goToLiveDetail(liveId) {
      this.$router.push({
        name: "LiveDetail",
        params: { slug: liveId },
      });
    },

    typeIconUrl(type) {
      return require(`@/assets/images/${type}.png`);
    },

    getLastLiveTitle(artist) {
      if (!artist.lives || artist.lives.length === 0) return "";
      const lastLive = artist.lives.reduce((latest, current) => {
        const latestDate = new Date(latest.date || 0);
        const currentDate = new Date(current.date || 0);
        return currentDate > latestDate ? current : latest;
      });
      return lastLive.title || "";
    },

    getFirstLiveTitle(artist) {
      if (!artist.lives || artist.lives.length === 0) return "";
      const firstLive = artist.lives.reduce((earliest, current) => {
        const earliestDate = new Date(earliest.date || 0);
        const currentDate = new Date(current.date || 0);
        return currentDate < earliestDate ? current : earliest;
      });
      return firstLive.title || "";
    },

    getLastLiveType(artist) {
      if (!artist.lives || artist.lives.length === 0) return "vocal";
      const lastLive = artist.lives.reduce((latest, current) => {
        const latestDate = new Date(latest.date || 0);
        const currentDate = new Date(current.date || 0);
        return currentDate > latestDate ? current : latest;
      });
      return lastLive.type || "vocal";
    },

    getFirstLiveType(artist) {
      if (!artist.lives || artist.lives.length === 0) return "vocal";
      const firstLive = artist.lives.reduce((earliest, current) => {
        const earliestDate = new Date(earliest.date || 0);
        const currentDate = new Date(current.date || 0);
        return currentDate < earliestDate ? current : earliest;
      });
      return firstLive.type || "vocal";
    },

    // 프로필 사진 업로드 관련 메서드들
    handleImageUpdated(artist, url) {
      artist.profileImage = url;
      // console.log(`Profile image updated for ${artist.name}: ${url}`);
    },

    handleImageRemoved(artist) {
      artist.profileImage = null;
      // console.log(`Profile image removed for ${artist.name}`);
    },

    getTypeDisplayName(type) {
      return getArtistTypeLabel(type);
    },

    // 승인 대기 필터 토글
    togglePendingFilter() {
      this.showPendingOnly = !this.showPendingOnly;

      if (!this.showPendingOnly) {
        // 승인 대기 필터를 끌 때 타입 필터를 전체로 복원
        this.selectedTypes = {
          all: true,
          vocal: false,
          band: false,
          mix: false,
        };
      }

      this.currentPage = 1; // 페이지 초기화

      // 필터 변경 후 이미지 로딩
      this.$nextTick(() => {
        this.loadProfileImagesForCurrentPage();
      });
    },

    // 아티스트 목록 로드 (멤버 관리용)
    async loadArtistsForMembers() {
      try {
        const artistsSnapshot = await getDocs(collection(firestore, "artists"));
        this.artistsWithTypes = artistsSnapshot.docs
          .map((doc) => {
            const data = doc.data();
            return {
              name: data.name,
              type: data.type || null,
              approved: data.approved || false,
            };
          })
          .filter((artist) => artist.approved);

        this.allArtists = this.artistsWithTypes.map((artist) => artist.name);
      } catch (error) {
        console.error("아티스트 목록 로드 오류:", error);
        this.allArtists = [];
        this.artistsWithTypes = [];
      }
    },
  },
};
</script>

<style module>
.container {
  width: 100%;
  max-width: 1024px;
  margin: 0 auto;
  padding: 0 15px;
  min-height: 100vh;
  padding-bottom: 50px;
}

.header {
  text-align: center;
  padding: 25px;
  border-radius: 15px;
  position: relative;
}

.titleSection {
  margin-bottom: 15px;
}

.title {
  font-size: 2.2rem;
  color: #333;
  margin: 0 0 10px 0;
  font-weight: bold;
}

.subtitle {
  font-size: 1rem;
  color: #666;
  margin: 0;
}

.error {
  text-align: center;
  padding: 60px 20px;
  border-radius: 15px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.retryButton {
  background: #3498db;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  margin-top: 15px;
}

.retryButton:hover {
  background: #2980b9;
}

.summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  color: #333;
  margin-bottom: 20px;
}

.summaryCard {
  background: white;
  text-align: center;
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}

.summaryCard:hover {
  transform: translateY(-2px);
}

.summaryCard h3 {
  margin: 0 0 8px 0;
  font-size: 0.85rem;
  font-weight: 500;
  color: #666;
  opacity: 0.9;
  line-height: 1.2;
}

.summaryNumber {
  margin: 0;
  font-size: 1.6rem;
  font-weight: bold;
  color: #333;
  line-height: 1.1;
}

.controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0px;
  flex-wrap: wrap;
  gap: 12px;
}

.searchBox {
  flex: 1;
  max-width: 350px;
}

.searchInput {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: border-color 0.3s;
  background: white;
}

.searchInput:focus {
  outline: none;
  border-color: #007bff;
}

.typeFilter {
  display: flex;
  gap: 6px;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 4px;
  scrollbar-width: thin;
  scrollbar-color: rgba(102, 189, 255, 0.3) transparent;
}

.typeFilter::-webkit-scrollbar {
  height: 6px;
}

.typeFilter::-webkit-scrollbar-track {
  background: transparent;
}

.typeFilter::-webkit-scrollbar-thumb {
  background: rgba(102, 189, 255, 0.3);
  border-radius: 3px;
}

.typeFilter::-webkit-scrollbar-thumb:hover {
  background: rgba(102, 189, 255, 0.5);
}

.typeButton {
  padding: 7px 14px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  background: white;
  color: #666;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}

.typeButton.activeType {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.filterBox {
  min-width: 150px;
}

.sortSelect {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
}

.artistList {
  padding: 15px 0px;
}

.artistCard {
  display: grid;
  grid-template-columns: 50px 1fr;
  gap: 15px;
  padding: 18px;
  margin-bottom: 12px;
  border-radius: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.artistCard:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

.rank {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  font-weight: bold;
  color: #007bff;
  border-radius: 50%;
  width: 45px;
  height: 45px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.artistInfo {
  flex: 1;
}

.artistName {
  margin: 0 0 8px 0;
  font-size: 1.2rem;
  color: #333;
  font-weight: bold;
}

.artistCount {
  margin: 0 0 10px 0;
  color: #007bff;
  font-weight: 600;
  font-size: 1rem;
}

.liveList {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.liveTag {
  color: #666;
  padding: 3px 7px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.2s ease;
  border: 1px solid #e9ecef;
}

.liveTag:hover {
  background: #007bff;
  color: white;
  transform: translateY(-1px);
  border-color: #007bff;
}

.typeIcon {
  width: 11px;
  height: 11px;
  margin-right: 5px;
  vertical-align: middle;
  filter: brightness(0) saturate(100%);
}

.liveTag:hover .typeIcon {
  filter: brightness(0) saturate(100%) invert(100%);
}

.noLivesMessage {
  background: #f8f9fa;
  color: #6c757d;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-style: italic;
  text-align: center;
  border: 1px solid #e9ecef;
}

/* 작성자 정보 스타일 */
.authorSection {
  margin: 12px 0;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #66bdff;
}

.authorInfo {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
}

.authorLabel {
  color: #666;
  font-weight: 500;
}

.authorName {
  color: #333;
  font-weight: 600;
  background: #e3f2fd;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.85rem;
}

.modifierName {
  color: #333;
  font-weight: 600;
  background: #fff3e0;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.85rem;
}

.registrationSource {
  color: #666;
  font-weight: 500;
  background: #f5f5f5;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 0.75rem;
  margin-left: 6px;
}

.authorDate {
  color: #888;
  font-size: 0.8rem;
  margin-left: auto;
}

.moreTag {
  background: #f5f5f5;
  color: #666;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
}

.artistStats {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 15px;
  border-radius: 8px;
  /* box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); */
  /* background: #f8f9fa; */
}

.statItem {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 6px;
  background: white;
  /* box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); */
  transition: all 0.2s ease;
}

.statItem:hover {
  transform: translateY(-1px);
  /* box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15); */
}

.statLabel {
  font-size: 0.75rem;
  color: #888;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.statValue {
  font-size: 0.9rem;
  color: #333;
  font-weight: 600;
  line-height: 1.3;
}

.liveName {
  display: inline-block;
  color: #666;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
  border: 1px solid #e9ecef;
  background: white;
  cursor: pointer;
}

.liveName:hover {
  background: #007bff;
  color: white;
  transform: translateY(-1px);
  border-color: #007bff;
}

/* 페이지네이션 스타일 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 25px;
  gap: 6px;
  flex-wrap: wrap;
}

.pageButton {
  padding: 7px 11px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #ffffff;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 34px;
  text-align: center;
}

.pageButton:hover:not(.activePage):not(:disabled) {
  background: #f8f9fa;
  border-color: #ccc;
  color: #333;
}

.pageButton:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  color: #ccc;
  background: #f8f9fa;
}

.activePage {
  background: #222;
  color: #ffffff;
  border-color: #222;
}

/* 아티스트 상세 정보 섹션 */
.detailSection {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ========================================
   Small (~480px)
   ======================================== */
@media (max-width: 480px) {
  .container {
    width: 100%;
    padding: 0 10px;
    padding-bottom: 40px;
  }

  .header {
    padding: 15px 10px;
    border-radius: 10px;
  }

  .title {
    margin-bottom: 0;
  }

  .typeSelectContainer {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 0;
  }

  .typeDropdown {
    flex: 1;
  }

  .typeSaveButton {
    padding: 7px 14px;
    background: #66bdff;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 13px;
    font-family: "NanumSquareRoundR", sans-serif;
    cursor: pointer;
    transition: background 0.2s ease;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .typeSaveButton:hover:not(:disabled) {
    background: #4a9eff;
  }

  .typeSaveButton:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  .typeCancelButton {
    padding: 7px 14px;
    background: #6c757d;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 13px;
    font-family: "NanumSquareRoundR", sans-serif;
    cursor: pointer;
    transition: background 0.2s ease;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .typeCancelButton:hover {
    background: #5a6268;
  }

  .selectedTypes {
    font-size: 12px;
    color: #666;
    margin-top: 8px;
  }

  :global(.p-dropdown-clear-icon) {
    color: #fff;
    display: none;
  }

  :global(.p-dropdown-clear-icon:hover) {
    color: #fff;
  }

  /* PrimeVue Dropdown 스타일링 */
  :global(.p-dropdown) {
    width: 100%;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: white;
    font-family: "NanumSquareRoundR", sans-serif;
  }

  :global(.p-dropdown:not(.p-disabled):hover) {
    border-color: #66bdff;
  }

  :global(.p-dropdown:not(.p-disabled).p-focus) {
    border-color: #66bdff;
    box-shadow: 0 0 0 2px rgba(102, 189, 255, 0.2);
  }

  :global(.p-dropdown .p-dropdown-label) {
    font-size: 13px;
    color: #333;
    padding: 7px;
    font-family: "NanumSquareRoundR", sans-serif;
  }

  :global(.p-dropdown .p-dropdown-trigger) {
    background: #f8f9fa;
    border-left: 1px solid #ddd;
    color: #666;
    width: 2.5rem;
  }

  :global(.p-dropdown .p-dropdown-trigger:hover) {
    background: #e9ecef;
    color: #333;
  }

  :global(.p-dropdown-panel) {
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    background: white;
    z-index: 800;
  }

  :global(.p-dropdown-panel .p-dropdown-items) {
    padding: 0;
  }

  :global(.p-dropdown-panel .p-dropdown-item) {
    padding: 9px 11px;
    font-size: 13px;
    color: #333;
    font-family: "NanumSquareRoundR", sans-serif;
    transition: all 0.2s ease;
  }

  :global(.p-dropdown-panel .p-dropdown-item:hover) {
    background: #f8f9fa;
    color: #333;
  }

  :global(.p-dropdown-panel .p-dropdown-item.p-highlight) {
    background: #66bdff;
    color: white;
  }

  :global(.p-dropdown-panel .p-dropdown-item.p-focus) {
    background: #e3f2fd;
    color: #1976d2;
  }

  /* ========================================
   Small (~480px)
   ======================================== */
  @media (max-width: 480px) {
    .container {
      width: 100%;
      padding: 0 10px;
      padding-bottom: 40px;
    }

    .header {
      padding: 15px 10px;
      border-radius: 10px;
    }

    .title {
      font-size: 1.6rem;
    }

    .subtitle {
      font-size: 0.85rem;
    }

    .error {
      padding: 40px 15px;
      border-radius: 10px;
    }

    .retryButton {
      padding: 8px 16px;
      font-size: 0.9rem;
    }

    .summary {
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
      margin-bottom: 15px;
    }

    .summaryCard {
      padding: 12px 8px;
      border-radius: 8px;
    }

    .summaryCard h3 {
      font-size: 0.7rem;
      margin-bottom: 6px;
    }

    .summaryNumber {
      font-size: 1.1rem;
    }

    .controls {
      flex-direction: column;
      padding: 12px 0;
      gap: 10px;
    }

    .searchBox {
      max-width: 100%;
      width: 100%;
    }

    .searchInput {
      padding: 10px 12px;
      font-size: 0.9rem;
      border-radius: 6px;
    }

    .typeFilter {
      width: 100%;
      gap: 5px;
      overflow-x: auto;
      padding-bottom: 5px;
    }

    .typeButton {
      padding: 6px 12px;
      font-size: 0.8rem;
      border-radius: 6px;
    }

    .artistList {
      padding: 10px 0;
    }

    .artistCard {
      grid-template-columns: 1fr;
      gap: 12px;
      padding: 15px;
      margin-bottom: 10px;
      border-radius: 10px;
      text-align: center;
    }

    .rank {
      margin: 0 auto;
      font-size: 1.2rem;
      width: 40px;
      height: 40px;
    }

    .artistName {
      font-size: 1.1rem;
      margin-bottom: 6px;
    }

    .artistCount {
      font-size: 0.95rem;
      margin-bottom: 8px;
    }

    .liveList {
      gap: 6px;
      justify-content: center;
    }

    .liveTag {
      padding: 3px 6px;
      font-size: 0.7rem;
      border-radius: 5px;
    }

    .typeIcon {
      width: 10px;
      height: 10px;
      margin-right: 4px;
    }

    .noLivesMessage {
      padding: 6px 10px;
      font-size: 0.8rem;
    }

    .authorSection {
      margin: 10px 0;
      padding: 8px;
      border-radius: 6px;
      border-left-width: 3px;
    }

    .authorInfo {
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
      font-size: 0.8rem;
    }

    .authorLabel {
      font-size: 0.75rem;
    }

    .authorName,
    .modifierName {
      padding: 2px 6px;
      font-size: 0.75rem;
    }

    .registrationSource {
      padding: 1px 5px;
      font-size: 0.65rem;
      margin-left: 4px;
    }

    .authorDate {
      margin-left: 0;
      font-size: 0.7rem;
    }

    .detailSection {
      margin-top: 10px;
      gap: 8px;
    }

    .detailLinkSection,
    .typeSection,
    .membersSection {
      padding: 10px;
      border-radius: 5px;
    }

    .detailLinkInput {
      flex-direction: column;
      gap: 8px;
    }

    .detailInput {
      padding: 8px 10px;
      font-size: 13px;
      border-radius: 4px;
    }

    .saveButton,
    .cancelButton {
      width: 100%;
      padding: 10px 14px;
      font-size: 13px;
      border-radius: 6px;
    }

    .detailLinkDisplay {
      flex-direction: column;
      gap: 8px;
    }

    .detailLinkActions {
      width: 100%;
    }

    .editButton,
    .typeEditButton {
      width: 100%;
      padding: 8px 12px;
      font-size: 12px;
      border-radius: 4px;
    }

    .editActions {
      flex-direction: column;
      gap: 6px;
      width: 100%;
    }

    .typeDisplay {
      flex-direction: column;
      gap: 8px;
    }

    .typeDisplayText {
      font-size: 13px;
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
    }

    .typeTag {
      font-size: 10px;
      padding: 2px 6px;
    }

    .categorySelection,
    .positionSelection {
      margin-bottom: 15px;
    }

    .categorySelection h5,
    .positionSelection h5 {
      font-size: 12px;
      margin-bottom: 8px;
    }

    .radioGroup {
      flex-direction: column;
      gap: 8px;
    }

    .radioGroup label {
      font-size: 12px;
      gap: 5px;
    }

    .checkboxGroup {
      gap: 6px;
      margin-bottom: 10px;
    }

    .checkboxGroup label {
      font-size: 12px;
      gap: 5px;
    }

    .selectedPositions {
      padding: 10px;
      margin-top: 10px;
      border-radius: 6px;
    }

    .selectedPositions h6 {
      font-size: 11px;
      margin-bottom: 8px;
    }

    .positionList {
      gap: 6px;
    }

    .positionItem {
      padding: 6px 8px;
      gap: 6px;
      border-radius: 5px;
    }

    .positionRank {
      width: 16px;
      height: 16px;
      font-size: 9px;
    }

    .positionName {
      font-size: 11px;
    }

    .positionControls {
      gap: 3px;
    }

    .moveButton,
    .removeButton {
      width: 20px;
      height: 20px;
      font-size: 9px;
      border-radius: 3px;
    }

    .typeEditActions {
      flex-direction: column;
      gap: 6px;
      margin-top: 12px;
    }

    .typeSaveButton,
    .typeCancelButton {
      width: 100%;
      padding: 10px 14px;
      font-size: 13px;
      border-radius: 6px;
    }

    .membersSection {
      padding: 10px;
      border-radius: 5px;
    }

    .membersSectionHeader {
      flex-direction: column;
      align-items: stretch;
      gap: 8px;
      margin-bottom: 10px;
      font-size: 12px;
    }

    .membersEditButton {
      width: 100%;
      padding: 8px 12px;
      font-size: 12px;
      border-radius: 4px;
    }

    .membersDisplay {
      gap: 5px;
      margin-bottom: 6px;
    }

    .memberDisplayTag {
      padding: 3px 7px;
      font-size: 10px;
      border-radius: 10px;
    }

    .memberDisplayType {
      font-size: 9px;
    }

    .noMembersMessage {
      padding: 6px 10px;
      font-size: 11px;
      border-radius: 4px;
    }

    .membersEditContainer {
      gap: 8px;
    }

    .selectedMembers {
      gap: 5px;
      margin-bottom: 8px;
      padding: 6px;
      min-height: 32px;
      border-radius: 5px;
    }

    .memberTag {
      padding: 4px 8px;
      font-size: 11px;
      border-radius: 12px;
      gap: 4px;
    }

    .memberType {
      font-size: 10px;
    }

    .membersEditActions {
      flex-direction: column;
      gap: 6px;
    }

    .membersSaveButton,
    .membersCancelButton {
      width: 100%;
      padding: 10px 14px;
      font-size: 13px;
      border-radius: 6px;
    }

    .pagination {
      margin-top: 20px;
      gap: 4px;
    }

    .pageButton {
      padding: 6px 9px;
      font-size: 12px;
      min-width: 28px;
      border-radius: 5px;
    }

    :global(.p-dropdown) {
      border-radius: 6px;
    }

    :global(.p-dropdown .p-dropdown-label) {
      font-size: 12px;
      padding: 8px;
    }

    :global(.p-dropdown .p-dropdown-trigger) {
      width: 2rem;
    }

    :global(.p-dropdown-panel) {
      border-radius: 6px;
    }

    :global(.p-dropdown-panel .p-dropdown-item) {
      font-size: 12px;
      padding: 10px;
    }
  }

  /* 승인 관련 스타일 */
  .pendingButton {
    background: #ff6b9d !important;
    color: white !important;
  }

  .pendingButton:hover {
    background: #ff4d7d !important;
  }

  .membersSection {
    padding: 10px;
    border-radius: 5px;
  }

  .membersSectionHeader {
    padding: 5px 10px;
    background: #66bdff;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 11px;
    font-family: "NanumSquareRoundR", sans-serif;
    cursor: pointer;
    transition: background 0.2s ease;
  }

  .membersEditButton:hover {
    background: #4a9eff;
  }

  .membersDisplay {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 6px;
  }

  .memberDisplayTag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #e3f2fd;
    color: #1976d2;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-family: "NanumSquareRoundR", sans-serif;
    border: 1px solid #bbdefb;
  }

  .memberDisplayType {
    opacity: 0.7;
    font-size: 10px;
    font-weight: normal;
  }

  .noMembersMessage {
    background: #f8f9fa;
    color: #6c757d;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    font-style: italic;
    text-align: center;
    border: 1px solid #e9ecef;
  }

  .membersEditContainer {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  /* 멤버 관련 스타일 (ArtistForm에서 복사) */
  .selectedMembers {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 10px;
    padding: 7px;
    background: #f8f9fa;
    border-radius: 6px;
    min-height: 36px;
    align-items: center;
  }

  .memberTag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #66bdff;
    color: white;
    padding: 5px 10px;
    border-radius: 16px;
    font-size: 12px;
    font-family: "NanumSquareRoundR", sans-serif;
  }

  .memberType {
    opacity: 0.8;
    font-size: 11px;
    font-weight: normal;
  }

  .removeButton {
    background: rgba(255, 255, 255, 0.3);
    border: none;
    color: white;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 12px;
    line-height: 1;
    transition: background 0.2s ease;
  }

  .removeButton:hover {
    background: rgba(255, 255, 255, 0.5);
  }

  .removeButton:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .autocomplete {
    width: 100%;
  }

  .memberAutocomplete {
    font-family: "NanumSquareRoundR", sans-serif;
  }

  .membersEditActions {
    display: flex;
    gap: 6px;
    align-items: center;
  }

  .membersSaveButton {
    padding: 7px 14px;
    background: #66bdff;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 13px;
    font-family: "NanumSquareRoundR", sans-serif;
    cursor: pointer;
    transition: background 0.2s ease;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .membersSaveButton:hover:not(:disabled) {
    background: #4a9eff;
  }

  .membersSaveButton:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  .membersCancelButton {
    padding: 7px 14px;
    background: #6c757d;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 13px;
    font-family: "NanumSquareRoundR", sans-serif;
    cursor: pointer;
    transition: background 0.2s ease;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .membersCancelButton:hover {
    background: #5a6268;
  }

  /* 새로운 타입 편집 스타일 */
  .categorySelection {
    margin-bottom: 15px;
  }

  .categorySelection h5 {
    margin: 0 0 8px 0;
    font-size: 13px;
    font-weight: bold;
    color: #495057;
    font-family: "NanumSquareRoundB", sans-serif;
  }

  .radioGroup {
    display: flex;
    gap: 12px;
  }

  .radioGroup label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-family: "NanumSquareRoundR", sans-serif;
    cursor: pointer;
  }

  .radioGroup input[type="radio"] {
    margin: 0;
    cursor: pointer;
  }

  .positionSelection {
    margin-bottom: 15px;
  }

  .positionSelection h5 {
    margin: 0 0 8px 0;
    font-size: 13px;
    font-weight: bold;
    color: #495057;
    font-family: "NanumSquareRoundB", sans-serif;
  }

  .checkboxGroup {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }

  .checkboxGroup label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-family: "NanumSquareRoundR", sans-serif;
    cursor: pointer;
  }

  .checkboxGroup input[type="checkbox"] {
    width: 10px;
    height: 10px;
    margin: 0;
    cursor: pointer;
  }

  .selectedPositions {
    margin-top: 12px;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
  }

  .selectedPositions h6 {
    margin: 0 0 8px 0;
    font-size: 12px;
    font-weight: bold;
    color: #495057;
    font-family: "NanumSquareRoundB", sans-serif;
  }

  .positionList {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .positionItem {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    transition: all 0.2s;
  }

  .positionItem:hover {
    border-color: #66bdff;
    box-shadow: 0 2px 4px rgba(102, 189, 255, 0.1);
  }

  .positionRank {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    background: #66bdff;
    color: white;
    border-radius: 50%;
    font-size: 10px;
    font-weight: bold;
    font-family: "NanumSquareRoundB", sans-serif;
  }

  .positionName {
    flex: 1;
    font-size: 12px;
    font-family: "NanumSquareRoundR", sans-serif;
    color: #495057;
  }

  .positionControls {
    display: flex;
    gap: 4px;
  }

  .moveButton,
  .removeButton {
    width: 22px;
    height: 22px;
    border: none;
    border-radius: 4px;
    font-size: 9px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .moveButton {
    background: #6c757d;
    color: white;
  }

  .moveButton:hover:not(:disabled) {
    background: #5a6268;
  }

  .moveButton:disabled {
    background: #e9ecef;
    color: #adb5bd;
    cursor: not-allowed;
  }

  .removeButton {
    background: #dc3545;
    color: white;
  }

  .removeButton:hover:not(:disabled) {
    background: #c82333;
  }

  .removeButton:disabled {
    background: #e9ecef;
    color: #adb5bd;
    cursor: not-allowed;
  }

  .typeEditActions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
  }
}
</style>